import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { isLocked } from '../../lib/workspaces';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/Toast';
import { AddPromptModal } from '../../components/AddPromptModal';
import { DeleteModal } from '../../components/DeleteModal';
import { InfoTooltip } from '../../components/InfoTooltip';
import { OutOfCreditsModal } from '../../components/OutOfCreditsModal';
import { LLM_MODELS } from '../../lib/llmModels';
import type { SuggestedPrompt, TrackedPrompt } from '../../types/database';

const PAGE_SIZE = 10;
const parseDateValue = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value: string | null | undefined, fallback = '—'): string => {
  const parsed = parseDateValue(value);
  if (!parsed) return fallback;

  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const normalizePromptText = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, ' ');

const formatRelativeTime = (value: string | null): string => {
  const parsed = parseDateValue(value);
  if (!parsed) return 'Not searched yet';

  const timestamp = parsed.getTime();

  const elapsedMs = Date.now() - timestamp;
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (elapsedMs < minuteMs) return 'Just now';
  if (elapsedMs < hourMs) {
    const minutes = Math.max(1, Math.floor(elapsedMs / minuteMs));
    return `${minutes}min ago`;
  }
  if (elapsedMs < dayMs) {
    const hours = Math.max(1, Math.floor(elapsedMs / hourMs));
    return `${hours}hr${hours === 1 ? '' : 's'} ago`;
  }
  if (elapsedMs < 7 * dayMs) {
    const days = Math.max(1, Math.floor(elapsedMs / dayMs));
    return `${days}d ago`;
  }

  return formatDate(parsed.toISOString());
};

const formatTimeUntil = (target: Date | null): string => {
  if (!target) return 'Pending';

  const diffMs = target.getTime() - Date.now();
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs <= 0) return 'Due now';
  if (diffMs < hourMs) {
    const minutes = Math.max(1, Math.ceil(diffMs / minuteMs));
    return `${minutes}mins`;
  }
  if (diffMs < dayMs) {
    const hours = Math.max(1, Math.ceil(diffMs / hourMs));
    return `${hours}hrs`;
  }
  if (diffMs < 7 * dayMs) {
    const days = Math.max(1, Math.ceil(diffMs / dayMs));
    return `${days}d`;
  }

  return formatDate(target.toISOString());
};

const getPromptLastSearchedAt = (
  prompt: TrackedPrompt,
  workspaceLastRunAt: string | null | undefined
): string | null => {
  if (prompt.last_crawled_at) return prompt.last_crawled_at;
  if (!workspaceLastRunAt) return null;

  const promptCreatedAt = parseDateValue(prompt.created_at);
  const workspaceLastRun = parseDateValue(workspaceLastRunAt);

  if (!promptCreatedAt || !workspaceLastRun) return null;
  if (promptCreatedAt.getTime() > workspaceLastRun.getTime()) return null;

  return workspaceLastRunAt;
};

export function Prompts() {
  const { workspace } = useAuthStore();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddingPrompt, setIsAddingPrompt] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdatingPrompt, setIsUpdatingPrompt] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isRemovingPrompt, setIsRemovingPrompt] = useState(false);
  const [isDiscoveringSuggestions, setIsDiscoveringSuggestions] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [promptToRemove, setPromptToRemove] = useState<TrackedPrompt | null>(null);
  const [promptToEdit, setPromptToEdit] = useState<TrackedPrompt | null>(null);
  const [promptPage, setPromptPage] = useState(1);
  const autoDiscoveryAttemptedForWorkspace = useRef<string | null>(null);

  const { data: prompts = [], refetch: refetchPrompts } = useQuery({
    queryKey: ['tracked_prompts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('tracked_prompts')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as TrackedPrompt[];
    },
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    if (!workspace?.id || !workspace?.last_prompt_run_at) return;
    refetchPrompts();
  }, [workspace?.id, workspace?.last_prompt_run_at, refetchPrompts]);

  const { data: suggestedPrompts = [] } = useQuery({
    queryKey: ['suggested_prompts', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from('suggested_prompts')
        .select('*')
        .eq('workspace_id', workspace.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data as SuggestedPrompt[];
    },
    enabled: !!workspace?.id,
  });

  const { data: promptSearchStats } = useQuery({
    queryKey: ['prompt_search_stats', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return { total: 0, lastSearchedAt: null as string | null };

      const [{ count, error: countError }, { data: latestRows, error: latestError }] = await Promise.all([
        supabase
          .from('prompt_responses')
          .select('id', { count: 'exact', head: true })
          .eq('workspace_id', workspace.id),
        supabase
          .from('prompt_responses')
          .select('created_at')
          .eq('workspace_id', workspace.id)
          .order('created_at', { ascending: false })
          .limit(1),
      ]);

      if (countError) {
        if (countError.code === '42P01') return { total: 0, lastSearchedAt: null as string | null };
        throw countError;
      }

      if (latestError) {
        if (latestError.code === '42P01') return { total: count ?? 0, lastSearchedAt: null as string | null };
        throw latestError;
      }

      return {
        total: count ?? 0,
        lastSearchedAt: latestRows?.[0]?.created_at ?? null,
      };
    },
    enabled: !!workspace?.id,
  });

  useEffect(() => {
    const discoverSuggestedPrompts = async () => {
      // A Locked workspace's suggestions are hidden, not missing.
      if (!workspace?.id || !workspace.brand_url || isLocked(workspace)) return;
      if (autoDiscoveryAttemptedForWorkspace.current === workspace.id) return;
      if (suggestedPrompts.length > 0) {
        autoDiscoveryAttemptedForWorkspace.current = workspace.id;
        return;
      }

      autoDiscoveryAttemptedForWorkspace.current = workspace.id;
      setIsDiscoveringSuggestions(true);
      try {
        const { error } = await supabase.functions.invoke('find-suggested-prompts', {
          body: {
            workspace_id: workspace.id,
            brand_url: workspace.brand_url,
          },
        });

        if (error) throw error;

        await queryClient.invalidateQueries({ queryKey: ['suggested_prompts', workspace.id] });
      } catch (error) {
        console.error('Error discovering suggested prompts:', error);
      } finally {
        setIsDiscoveringSuggestions(false);
      }
    };

    discoverSuggestedPrompts();
  }, [workspace, suggestedPrompts.length, queryClient]);

  const totalPromptSearches = promptSearchStats?.total ?? 0;
  const totalPromptsTracked = prompts.length;
  const isTrial = workspace?.state === 'trial';
  const totalPromptLimit = workspace?.prompt_limit ?? 0;
  const hasReachedPromptLimit = totalPromptLimit > 0 && totalPromptsTracked >= totalPromptLimit;
  // Nothing is collected with no prompts, so the last one can't be removed (#3).
  const isLastPrompt = totalPromptsTracked === 1;
  // run-prompts runs only the newest N (prompts are already newest first), so mirror that here.
  const notRunPromptIds = useMemo(
    () => new Set(totalPromptLimit > 0 ? prompts.slice(totalPromptLimit).map((p) => p.id) : []),
    [prompts, totalPromptLimit]
  );
  const nextPromptSearchAt = useMemo(() => {
    if (prompts.length === 0) return null;
    if (!workspace?.last_prompt_run_at) return null;

    const lastRunTimestamp = new Date(workspace.last_prompt_run_at).getTime();
    if (Number.isNaN(lastRunTimestamp)) return null;

    return new Date(lastRunTimestamp + 24 * 60 * 60 * 1000);
  }, [prompts.length, workspace?.last_prompt_run_at]);
  const nextPromptSearchLabel = prompts.length === 0
    ? '—'
    : formatTimeUntil(nextPromptSearchAt);

  const lastSearchedAt = promptSearchStats?.lastSearchedAt ?? null;

  const hasPromptPagination = prompts.length > PAGE_SIZE;
  const totalPromptPages = Math.max(1, Math.ceil(prompts.length / PAGE_SIZE));

  useEffect(() => {
    if (promptPage > totalPromptPages) {
      setPromptPage(totalPromptPages);
    }
  }, [promptPage, totalPromptPages]);

  const paginatedPrompts = useMemo(() => {
    const start = (promptPage - 1) * PAGE_SIZE;
    return prompts.slice(start, start + PAGE_SIZE);
  }, [prompts, promptPage]);

  const handleAddPrompt = async (rawPromptText: string) => {
    if (!workspace?.id) return;

    setIsAddingPrompt(true);
    try {
      if (hasReachedPromptLimit) {
        setIsUpgradeModalOpen(true);
        return;
      }

      const promptText = rawPromptText.trim();
      if (!promptText) {
        toast.error('Please enter a prompt');
        return;
      }

      const normalizedPromptText = normalizePromptText(promptText);
      const exists = prompts.some((item) => normalizePromptText(item.text) === normalizedPromptText);
      if (exists) {
        toast.error('This prompt is already in your list');
        return;
      }

      const { error: insertError } = await supabase.from('tracked_prompts').insert({
        workspace_id: workspace.id,
        text: promptText,
      });

      if (insertError) throw insertError;

      const matchedSuggestion = suggestedPrompts.find(
        (item) => normalizePromptText(item.text) === normalizedPromptText
      );

      if (matchedSuggestion) {
        await supabase.from('suggested_prompts').delete().eq('id', matchedSuggestion.id);
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] }),
        queryClient.invalidateQueries({ queryKey: ['suggested_prompts', workspace.id] }),
      ]);

      setIsAddModalOpen(false);
      toast.success('Prompt added');
    } catch (error: any) {
      console.error('Error adding prompt:', error);
      toast.error(error?.message || 'Failed to add prompt');
    } finally {
      setIsAddingPrompt(false);
    }
  };

  const handleAddSuggestedPrompt = async (suggestion: SuggestedPrompt) => {
    if (!workspace?.id) return;

    try {
      if (hasReachedPromptLimit) {
        setIsUpgradeModalOpen(true);
        return;
      }

      const normalizedPromptText = normalizePromptText(suggestion.text);
      const exists = prompts.some((item) => normalizePromptText(item.text) === normalizedPromptText);
      if (exists) {
        toast.error('This prompt is already in your list');
        return;
      }

      const { error: insertError } = await supabase.from('tracked_prompts').insert({
        workspace_id: workspace.id,
        text: suggestion.text,
      });
      if (insertError) throw insertError;

      const { error: deleteError } = await supabase
        .from('suggested_prompts')
        .delete()
        .eq('id', suggestion.id);
      if (deleteError) throw deleteError;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] }),
        queryClient.invalidateQueries({ queryKey: ['suggested_prompts', workspace.id] }),
      ]);

      toast.success('Prompt added from suggestions');
    } catch (error: any) {
      console.error('Error adding suggested prompt:', error);
      toast.error(error?.message || 'Failed to add suggested prompt');
    }
  };

  const openRemovePromptModal = (prompt: TrackedPrompt) => {
    if (isLastPrompt) {
      toast.info("You need at least one prompt. Lima stops tracking your brand without one, so add another prompt before removing this one, or edit this one instead.");
      return;
    }
    setPromptToRemove(prompt);
    setIsDeleteModalOpen(true);
  };

  const openEditPromptModal = (prompt: TrackedPrompt) => {
    setPromptToEdit(prompt);
    setIsEditModalOpen(true);
  };

  const handleUpdatePrompt = async (rawPromptText: string) => {
    if (!workspace?.id || !promptToEdit) return;

    setIsUpdatingPrompt(true);
    try {
      const promptText = rawPromptText.trim();
      if (!promptText) {
        toast.error('Please enter a prompt');
        return;
      }

      const normalizedPromptText = normalizePromptText(promptText);
      const exists = prompts.some(
        (item) => item.id !== promptToEdit.id && normalizePromptText(item.text) === normalizedPromptText
      );
      if (exists) {
        toast.error('This prompt is already in your list');
        return;
      }

      const { error } = await supabase
        .from('tracked_prompts')
        .update({ text: promptText })
        .eq('id', promptToEdit.id);

      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] });
      setIsEditModalOpen(false);
      setPromptToEdit(null);
      toast.success('Prompt updated');
    } catch (error: any) {
      console.error('Error updating prompt:', error);
      toast.error(error?.message || 'Failed to update prompt');
    } finally {
      setIsUpdatingPrompt(false);
    }
  };

  const handleConfirmRemovePrompt = async () => {
    if (!workspace?.id || !promptToRemove) return;

    setIsRemovingPrompt(true);
    try {
      const { error } = await supabase.from('tracked_prompts').delete().eq('id', promptToRemove.id);
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ['tracked_prompts', workspace.id] });
      setIsDeleteModalOpen(false);
      setPromptToRemove(null);
      toast.success('Prompt removed');
    } catch (error: any) {
      console.error('Error removing prompt:', error);
      toast.error(error?.message || 'Failed to remove prompt');
    } finally {
      setIsRemovingPrompt(false);
    }
  };

  return (
    <div>
      <div className="spacer-xsmall"></div>

      <div className="overview-header" style={{ marginBottom: '1.5rem' }}>
        <div className="overview-header-left">
          <h1>Prompts</h1>
        </div>
        <button
          className="button is-small w-button"
          onClick={() => {
            if (hasReachedPromptLimit) {
              setIsUpgradeModalOpen(true);
              return;
            }
            setIsAddModalOpen(true);
          }}
        >
          Add Prompt
        </button>
      </div>

      <div className="stat-cards-row">
        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Prompts</span>
          </div>
          <div className="stat-card-value">
            {totalPromptsTracked.toLocaleString()}/{totalPromptLimit.toLocaleString()}
          </div>
          <div className="stat-card-change is-neutral">
            {Math.max(totalPromptLimit - totalPromptsTracked, 0).toLocaleString()} remaining
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Next Prompt Search</span>
          </div>
          <div className="stat-card-value">{nextPromptSearchLabel}</div>
          <div className="stat-card-change is-neutral">
            {prompts.length === 0
              ? 'Add prompts to start scheduled runs'
              : workspace?.last_prompt_run_at
                ? `Last run: ${formatRelativeTime(workspace.last_prompt_run_at)}`
                : 'Waiting for first run'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">LLMs Searched</span>
          </div>
          <div className="stat-card-value is-llm-stack">
            <div className="llm-stack-tooltip-wrapper">
              <div className="llm-badge-stack">
                {LLM_MODELS.map((model) => (
                  <div
                    key={model.name}
                    className="llm-badge"
                    title={model.name}
                  >
                    {model.icon ? (
                      <img src={model.icon} alt={model.name} className="llm-badge-image" />
                    ) : (
                      model.short
                    )}
                  </div>
                ))}
              </div>
              <div className="llm-list-tooltip">
                {LLM_MODELS.map((model) => (
                  <div key={model.name} className="llm-list-item">
                    {model.icon ? (
                      <img src={model.icon} alt="" className="llm-list-item-icon" aria-hidden="true" />
                    ) : null}
                    {model.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="stat-card-change is-neutral">Hover a circle to see all models</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Prompt Searches</span>
          </div>
          <div className="stat-card-value">{totalPromptSearches.toLocaleString()}</div>
          <div className="stat-card-change is-neutral">
            Last searched: {formatRelativeTime(lastSearchedAt)}
          </div>
        </div>
      </div>

      <div className="dashboard-panel" style={{ marginBottom: '1.5rem' }}>
        <div className="dashboard-panel-header" style={{ padding: '1rem 1.75rem' }}>
          <div className="table-title-wrapper is-left">
            <div className="dashboard-panel-title">
              Prompt List <span className="number-pill">{prompts.length}</span>
            </div>
            <InfoTooltip text="Prompts tracked for recurring AI search runs, including last crawl time." />
          </div>
          {notRunPromptIds.size > 0 && (
            <div className="text-size-xsmall text-colour-alt-grey">
              Your plan allows {totalPromptLimit} prompts, so only your {totalPromptLimit} newest are being run.
              Remove {notRunPromptIds.size} to choose which ones are kept.
            </div>
          )}
        </div>
        <div className="dashboard-panel-body is-table">
          <div className="table-scroll-wrap is-prompts-table">
          <table className={`table-component competitors-table prompts-table ${hasPromptPagination ? 'has-pagination' : ''}`}>
            <thead className="table-header">
              <tr className="table-row is-header-row">
                <th className="table-head is-prompt">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Prompt</div>
                </th>
                <th className="table-head is-date-added">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Added</div>
                </th>
                <th className="table-head is-last-searched">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Last Searched</div>
                </th>
                <th className="table-head is-searches">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">
                    Searches
                  </div>
                </th>
                <th className="table-head is-action">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">
                    Actions
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {prompts.length === 0 ? (
                <tr className="table-row">
                  <td className="table-cell" colSpan={5} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                    <div className="text-size-small text-colour-light-grey">
                      Add prompts to start running searches.
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedPrompts.map((prompt) => {
                  const promptLastSearchedAt = getPromptLastSearchedAt(
                    prompt,
                    workspace?.last_prompt_run_at
                  );

                  return (
                    <tr key={prompt.id} className="table-row" style={{ cursor: 'default' }}>
                      <td className="table-cell is-prompt">
                        <div className="text-size-xsmall text-weight-medium prompt-table-text">{prompt.text}</div>
                      </td>
                      <td className="table-cell is-date-added">
                        <div className="text-size-xsmall text-colour-alt-grey">{formatDate(prompt.created_at)}</div>
                      </td>
                      <td className="table-cell is-last-searched">
                        <div className="text-size-xsmall text-colour-alt-grey">
                          {notRunPromptIds.has(prompt.id) ? 'Not run (over plan limit)' : formatRelativeTime(promptLastSearchedAt)}
                        </div>
                      </td>
                      <td className="table-cell is-searches">
                        <div className="text-size-xsmall text-colour-alt-grey text-weight-regular">
                          {(prompt.times_crawled || 0).toLocaleString()}
                        </div>
                      </td>
                      <td className="table-cell is-action">
                        <div className="table-action-icons">
                          <button
                            type="button"
                            className="remove-icon-holder"
                            onClick={() => openEditPromptModal(prompt)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0,
                              marginLeft: 0,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            aria-label="Edit prompt"
                            title="Edit prompt"
                          >
                            <span className="embed-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/>
                                <path d="m15 5 4 4"/>
                              </svg>
                            </span>
                          </button>
                          <button
                            type="button"
                            className="remove-icon-holder"
                            onClick={() => openRemovePromptModal(prompt)}
                            aria-disabled={isLastPrompt}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: isLastPrompt ? 'not-allowed' : 'pointer',
                              opacity: isLastPrompt ? 0.2 : undefined,
                              padding: 0,
                              marginLeft: 0,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                            aria-label="Remove prompt"
                            title={isLastPrompt ? 'Add another prompt before removing this one. Tracking stops with no prompts.' : 'Remove prompt'}
                          >
                            <span className="embed-icon">
                              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 6 6 18" />
                                <path d="m6 6 12 12" />
                              </svg>
                            </span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {hasPromptPagination && (
                <tr className="table-row is-pagination-row">
                  <td className="table-cell is-pagination" colSpan={5}>
                    <div className="pagination">
                      <button
                        type="button"
                        className="button is-small is-white has-inner-shadow w-button"
                        onClick={() => setPromptPage((page) => Math.max(1, page - 1))}
                        disabled={promptPage === 1}
                      >
                        Previous
                      </button>
                      <div className="text-size-small text-weight-medium text-colour-alt-grey">
                        Page {promptPage} of {totalPromptPages}
                      </div>
                      <button
                        type="button"
                        className="button is-small is-white has-inner-shadow w-button"
                        onClick={() => setPromptPage((page) => Math.min(totalPromptPages, page + 1))}
                        disabled={promptPage === totalPromptPages}
                      >
                        Next
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <div className="dashboard-panel">
        <div className="dashboard-panel-header" style={{ padding: '1rem 1.75rem' }}>
          <div className="table-title-wrapper is-left">
            <div className="dashboard-panel-title">Suggested Prompts</div>
            <InfoTooltip text="Additional prompt ideas generated from your brand profile and market context." />
          </div>
        </div>
        <div className="dashboard-panel-body is-table">
          {isDiscoveringSuggestions && (
            <div className="text-size-small text-colour-alt-grey" style={{ padding: '0.75rem 1.75rem 0' }}>
              Searching for suggested prompts...
            </div>
          )}
          <div className="table-scroll-wrap is-prompts-table">
          <table className="table-component competitors-table prompts-table prompts-table--suggested">
            <thead className="table-header">
              <tr className="table-row is-header-row">
                <th className="table-head is-prompt">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Prompt</div>
                </th>
                <th className="table-head is-date-found">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">Date Found</div>
                </th>
                <th className="table-head is-action-suggested">
                  <div className="text-size-small text-weight-semibold text-colour-alt-grey">
                    Action
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {suggestedPrompts.length === 0 ? (
                <tr className="table-row">
                  <td className="table-cell" colSpan={3} style={{ width: '100%', justifyContent: 'center', padding: '2rem 1rem' }}>
                    <div className="text-size-small text-colour-light-grey">
                      No suggestions yet.
                    </div>
                  </td>
                </tr>
              ) : (
                suggestedPrompts.map((prompt) => {
                  return (
                    <tr
                      key={prompt.id}
                      className="table-row"
                      style={{ cursor: 'default' }}
                    >
                      <td className="table-cell is-prompt">
                        <div className="text-size-xsmall text-weight-medium prompt-table-text">{prompt.text}</div>
                      </td>
                      <td className="table-cell is-date-found">
                        <div className="text-size-small text-colour-alt-grey">{formatDate(prompt.created_at)}</div>
                      </td>
                      <td className="table-cell is-action-suggested">
                        <button
                          type="button"
                          className="button is-tiny is-white has-inner-shadow w-button"
                          onClick={() => handleAddSuggestedPrompt(prompt)}
                          style={{ padding: '0.2rem 0.5rem', minHeight: '1.6rem' }}
                        >
                          + Add
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>
      </div>

      <AddPromptModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onConfirm={handleAddPrompt}
        loading={isAddingPrompt}
      />

      <AddPromptModal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (!isUpdatingPrompt) {
            setIsEditModalOpen(false);
            setPromptToEdit(null);
          }
        }}
        onConfirm={handleUpdatePrompt}
        loading={isUpdatingPrompt}
        title="Rename Prompt"
        label="Prompt text"
        placeholder="e.g. what are the best tools for small businesses?"
        confirmText="Save changes"
        initialValue={promptToEdit?.text ?? ''}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isRemovingPrompt) {
            setIsDeleteModalOpen(false);
            setPromptToRemove(null);
          }
        }}
        onConfirm={handleConfirmRemovePrompt}
        title="Remove prompt?"
        message={
          promptToRemove
            ? `This prompt will no longer be tracked: "${promptToRemove.text}"`
            : 'This prompt will no longer be tracked.'
        }
        confirmText="Yes, remove"
        cancelText="No, cancel"
        loading={isRemovingPrompt}
      />

      <OutOfCreditsModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title={isTrial ? "You've reached your free trial prompt limit" : "You've reached your prompt limit"}
        description={
          isTrial
            ? `The free trial is limited to ${totalPromptLimit} prompts.`
            : 'Upgrade your plan to track more prompts and expand your AI search coverage.'
        }
        benefits={
          isTrial
            ? [
                'Track more than 20 prompts',
                'Unlock higher plan limits immediately',
                'Expand your AI search coverage',
              ]
            : [
                'Track more prompts every month',
                'Monitor more competitor and brand mentions',
                'Unlock higher workspace search limits',
              ]
        }
        ctaLabel="Upgrade now"
      />

      <div className="spacer-large"></div>
    </div>
  );
}
